// ============================================================================
// ADMIN FACILITATORS — Tier 1 view for managing certified facilitator list
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  BadgeCheck, Plus, Trash2, Loader, AlertCircle, X, Search, CheckCircle
} from 'lucide-react';
import {
  listFacilitators,
  certifyUser,
  revokeUserCertification,
  FacilitatorSummary,
  FacilitatorTier,
  TIER_LABELS
} from '../services/studio-bookings';
import { listUsers, UserProfile } from '../services/userManagement';

function TierBadge({ tier }: { tier: FacilitatorTier }) {
  const cls = tier === 'tier1'
    ? 'bg-purple-50 text-purple-700 border border-purple-200'
    : 'bg-blue-50 text-blue-700 border border-blue-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
      {TIER_LABELS[tier]}
    </span>
  );
}

function FacilitatorTable({
  label,
  list,
  revoking,
  onRevoke
}: {
  label: string;
  list: FacilitatorSummary[];
  revoking: string | null;
  onRevoke: (f: FacilitatorSummary) => void;
}) {
  return (
    <section>
      <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-3">
        {label} <span className="font-normal normal-case text-slate-400">({list.length})</span>
      </p>
      {list.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-[#e2e8f0] rounded-2xl text-slate-400 text-sm">
          No facilitators certified yet
        </div>
      ) : (
        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#e2e8f0] bg-slate-50/60">
                <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Name</th>
                <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Email</th>
                <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Tier</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e2e8f0]">
              {list.map(f => (
                <tr key={f.userId} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-5 py-3.5 text-sm font-semibold text-navy">{f.displayName}</td>
                  <td className="px-5 py-3.5 text-sm text-slate-500">{f.email}</td>
                  <td className="px-5 py-3.5"><TierBadge tier={f.tier} /></td>
                  <td className="px-5 py-3.5 text-right">
                    <button
                      onClick={() => onRevoke(f)}
                      disabled={revoking === f.userId}
                      className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 disabled:opacity-40 transition-colors ml-auto"
                    >
                      {revoking === f.userId
                        ? <Loader className="w-3 h-3 animate-spin" />
                        : <Trash2 className="w-3.5 h-3.5" />}
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function AdminFacilitators() {
  const [facilitators, setFacilitators] = useState<FacilitatorSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showCertifyModal, setShowCertifyModal] = useState(false);
  const [allUsers, setAllUsers] = useState<UserProfile[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [selectedTier, setSelectedTier] = useState<FacilitatorTier>('tier2');
  const [certifying, setCertifying] = useState(false);
  const [certifyError, setCertifyError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  function load() {
    setLoading(true);
    listFacilitators()
      .then(setFacilitators)
      .catch(() => setError('Failed to load facilitators'))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);

  async function openCertifyModal() {
    setShowCertifyModal(true);
    setCertifyError(null);
    setSelectedUser(null);
    setUserSearch('');
    try {
      const result = await listUsers({ status: 'active' });
      const certifiedIds = new Set(facilitators.map(f => f.userId));
      setAllUsers(result.users.filter(u => !certifiedIds.has(u.userId)));
    } catch {
      setCertifyError('Failed to load users');
    }
  }

  async function handleCertify() {
    if (!selectedUser) return;
    setCertifying(true);
    setCertifyError(null);
    try {
      await certifyUser(selectedUser.userId, selectedTier);
      setShowCertifyModal(false);
      setSuccess(`${selectedUser.displayName} certified as ${TIER_LABELS[selectedTier]}`);
      load();
    } catch (err: any) {
      setCertifyError(err.response?.data?.error || 'Failed to certify user');
    } finally {
      setCertifying(false);
    }
  }

  async function handleRevoke(facilitator: FacilitatorSummary) {
    if (!window.confirm(`Remove ${facilitator.tier === 'tier1' ? 'Tier 1' : 'Tier 2'} certification from ${facilitator.displayName}?`)) return;
    setRevoking(facilitator.userId);
    try {
      await revokeUserCertification(facilitator.userId);
      setSuccess(`${facilitator.displayName}'s certification removed`);
      load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to revoke certification');
    } finally {
      setRevoking(null);
    }
  }

  const filteredUsers = allUsers.filter(u =>
    userSearch === '' ||
    u.displayName.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  const tier1 = facilitators.filter(f => f.tier === 'tier1');
  const tier2 = facilitators.filter(f => f.tier === 'tier2');

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
      <header className="flex items-center justify-between border-b border-slate-200 pb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <BadgeCheck size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Certified Facilitators</h1>
            <p className="text-slate-500 text-sm font-medium">Manage Tier 1 and Tier 2 facilitator certifications</p>
          </div>
        </div>
        <button
          onClick={openCertifyModal}
          className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
        >
          <Plus className="w-4 h-4" /> Certify user
        </button>
      </header>

      {error && (
        <div className="mb-5 p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-5 p-4 rounded-xl bg-green-50 border border-green-200 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-700 font-medium">{success}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center h-40 items-center">
          <Loader className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          <FacilitatorTable label="Tier 1 — Core Team" list={tier1} revoking={revoking} onRevoke={handleRevoke} />
          <FacilitatorTable label="Tier 2 — Certified Facilitators" list={tier2} revoking={revoking} onRevoke={handleRevoke} />
        </div>
      )}

      </div>
      {showCertifyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md border border-[#e2e8f0]">
            <div className="flex items-center justify-between p-5 border-b border-[#e2e8f0]">
              <h2 className="font-bold text-navy text-lg">Certify a user</h2>
              <button onClick={() => setShowCertifyModal(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-50">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-5">
              {certifyError && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-200">
                  <p className="text-sm text-red-700">{certifyError}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Search users</label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search by name or email…"
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    className="w-full border border-[#e2e8f0] rounded-xl pl-9 pr-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  />
                </div>
                <div className="mt-2 max-h-44 overflow-y-auto border border-[#e2e8f0] rounded-xl divide-y divide-[#e2e8f0]">
                  {filteredUsers.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No users found</p>
                  ) : filteredUsers.slice(0, 20).map(u => (
                    <button
                      key={u.userId}
                      type="button"
                      onClick={() => setSelectedUser(u)}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        selectedUser?.userId === u.userId
                          ? 'bg-primary/10 text-primary'
                          : 'hover:bg-slate-50 text-navy'
                      }`}
                    >
                      <span className="font-semibold">{u.displayName}</span>
                      <span className="ml-2 text-slate-400 text-xs">{u.email}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Certification tier</label>
                <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
                  {(['tier2', 'tier1'] as FacilitatorTier[]).map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setSelectedTier(t)}
                      className={`flex-1 py-2.5 text-sm font-semibold transition-all ${
                        selectedTier === t ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      {TIER_LABELS[t]}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  {selectedTier === 'tier1'
                    ? 'Tier 1 — Core team. Can approve bookings and manage the facilitator list.'
                    : 'Tier 2 — Certified facilitator. Can be selected to host sessions.'}
                </p>
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowCertifyModal(false)}
                  className="flex-1 py-2.5 border border-[#e2e8f0] rounded-xl text-sm font-medium text-slate-500 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCertify}
                  disabled={!selectedUser || certifying}
                  className="flex-1 py-2.5 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-60 transition-all shadow-md shadow-primary/20"
                >
                  {certifying ? 'Certifying…' : 'Certify'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
